/**
 * Utilities for building publish rules change details from v3 ops-based patches.
 */

export interface ListingRuleOp {
  opId: string
  kind: 'add' | 'remove' | 'replace'
  targetRuleId?: string
  before?: any
  after?: any
  changedAt: string
  changedBy?: string
  changedByUserId?: string
}

export interface ListingRulesPatchesV3 {
  [segmentId: string]: {
    ops: ListingRuleOp[]
    updatedAt: string
    updatedBy?: string
    updatedByUserId?: string
  }
}

export interface ListingRulesChangeDetails {
  hasChanges: boolean
  totalRules: number
  totalRulesAdded: number
  totalRulesRemoved: number
  segments: Record<
    string,
    {
      rulesCount: number
      addedRules: Array<{
        opId: string
        field: string
        operator: string
        value: unknown
        action?: string
        reason?: string
      }>
      modifiedRules: Array<{
        opId: string
        index?: number
        before: {
          field: string
          operator: string
          value: unknown
          action?: string
          reason?: string
        }
        after: {
          field: string
          operator: string
          value: unknown
          action?: string
          reason?: string
        }
      }>
      removedRules: Array<{
        opId: string
        index?: number
        field: string
        operator: string
        value: unknown
        action?: string
        reason?: string
      }>
      rulesAdded?: number
      rulesRemoved?: number
    }
  >
}

/**
 * Format a rule node for display (extract field/operator/value).
 */
function formatRuleForDisplay(rule: any): {
  field: string
  operator: string
  value: unknown
  action?: string
  reason?: string
} {
  // Handle both simple rules and groups
  let field = rule?.field || ''
  let operator = rule?.operator || rule?.logic || ''
  let value = rule?.value
  
  // For groups, try to extract from first child rule
  if ((!field || !operator) && Array.isArray(rule?.rules) && rule.rules.length > 0) {
    const first = rule.rules[0]
    field = first?.field || field
    operator = first?.operator || operator || rule?.logic || ''
    value = first?.value !== undefined ? first.value : value
  }
  
  return {
    field,
    operator,
    value,
    action: rule?.action,
    reason: rule?.reason || '',
  }
}

/**
 * Build publish rules change details from v3 ops-based patches.
 * Converts ops into UI-friendly "added/modified/removed" structure.
 */
export function buildListingRulesChangesFromOps(params: {
  patches: ListingRulesPatchesV3
}): ListingRulesChangeDetails {
  const { patches } = params
  
  const segments: ListingRulesChangeDetails['segments'] = {}
  let totalRulesAdded = 0
  let totalRulesRemoved = 0
  let totalRules = 0
  
  for (const [segmentId, segmentPatch] of Object.entries(patches)) {
    const ops = segmentPatch.ops || []
    
    const addedRules: ListingRulesChangeDetails['segments'][string]['addedRules'] = []
    const modifiedRules: ListingRulesChangeDetails['segments'][string]['modifiedRules'] = []
    const removedRules: ListingRulesChangeDetails['segments'][string]['removedRules'] = []
    
    for (const op of ops) {
      if (op.kind === 'add' && op.after) {
        const formatted = formatRuleForDisplay(op.after)
        addedRules.push({
          opId: op.opId,
          ...formatted,
        })
        totalRulesAdded++
      } else if (op.kind === 'remove' && op.before) {
        const formatted = formatRuleForDisplay(op.before)
        removedRules.push({
          opId: op.opId,
          ...formatted,
        })
        totalRulesRemoved++
      } else if (op.kind === 'replace' && op.before && op.after) {
        const before = formatRuleForDisplay(op.before)
        const after = formatRuleForDisplay(op.after)
        modifiedRules.push({
          opId: op.opId,
          before,
          after,
        })
      }
    }
    
    const rulesCount = addedRules.length + modifiedRules.length + removedRules.length
    totalRules += rulesCount
    
    segments[segmentId] = {
      rulesCount,
      addedRules,
      modifiedRules,
      removedRules,
      rulesAdded: addedRules.length,
      rulesRemoved: removedRules.length,
    }
  }
  
  return {
    hasChanges: totalRules > 0,
    totalRules,
    totalRulesAdded,
    totalRulesRemoved,
    segments,
  }
}
